import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MobileAuth } from "@/components/MobileAuth";

const inputCls =
  "w-full border border-slate-200 bg-[#FFFFFF]/60 px-4 py-3.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors duration-300 focus:border-[#6D28D9]/60";

export default function AuthPage() {
  const portal = window.location.pathname.startsWith("/client");
  const initialMode = window.location.pathname.endsWith("/register") ? "register" : window.location.pathname.endsWith("/forgot-password") ? "forgot" : "login";
  const [mode, setMode] = useState(initialMode);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [channel, setChannel] = useState("email");
  const navigate = useNavigate();

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/client/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setSent(true);
        return;
      }
      if (mode === "register") {
        const reg = await api.post("/auth/register", { name, email, password, phone: phone || null });
        if (reg.data.client_id) toast.success(`Account created · Your Client ID is ${reg.data.client_id}`);
        toast.success("Account created — welcome to PRE-IPO EXPERT.");
      } else {
        await api.post("/auth/login", { email, password });
        toast.success("Signed in successfully.");
      }
      const redirect = sessionStorage.getItem("post_auth_redirect");
      sessionStorage.removeItem("post_auth_redirect");
      navigate(redirect || "/client/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => { setMode(m); setError(""); setSent(false); };

  const heading = { login: "Welcome back", register: "Create your account", forgot: "Reset your password" }[mode];
  const sub = {
    login: "Sign in to track your enquiries and move faster through the desk.",
    register: "One account for enquiries, desk updates and research. Free, no obligation.",
    forgot: "Enter the email on your account and we'll send a secure link, valid for 30 minutes.",
  }[mode];

  return (
    <div data-testid="auth-page" className="hero-spotlight flex min-h-screen flex-col bg-[#FFFFFF] text-slate-900">
      <header className="border-b border-slate-200 bg-[#FFFFFF]/80 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
            <span className="font-display text-lg font-semibold tracking-[0.18em]">
              PRE-IPO <span className="text-[#6D28D9]">EXPERT</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="w-full max-w-md">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#6D28D9]/90">Investor Account</p>
          <h1 className="font-display mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">{heading}</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-500">{sub}</p>

          {mode === "forgot" && sent ? (
            <div data-testid="forgot-sent" className="mt-8 border border-emerald-600/30 bg-emerald-600/[0.06] p-8">
              <p className="font-display text-lg font-medium text-emerald-800">Check your inbox</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">If an account exists for <span className="text-slate-800">{email}</span>, a reset link is on its way. It expires in 30 minutes.</p>
              <button data-testid="forgot-back-to-login" type="button" onClick={() => switchMode("login")} className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#6D28D9] hover:text-[#5B21B6]">Back to sign in</button>
            </div>
          ) : channel === "mobile" && mode !== "forgot" ? (
            <MobileAuth onSwitchToEmail={() => setChannel("email")} />
          ) : (
          <form data-testid="auth-form" onSubmit={submit} className="mt-8 space-y-4 border border-slate-200 bg-[#F6F4FD]/70 p-8 backdrop-blur">
            {mode !== "forgot" && (
              <>
                <button
                  data-testid="auth-google-button"
                  type="button"
                  onClick={googleLogin}
                  className="flex w-full items-center justify-center gap-3 border border-slate-300 bg-violet-50/60 px-8 py-3.5 font-mono2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-800 transition-colors duration-300 hover:border-[#6D28D9]/50 hover:bg-violet-50"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
                  </svg>
                  Continue with Google
                </button>
                <button
                  data-testid="auth-mobile-button"
                  type="button"
                  onClick={() => setChannel("mobile")}
                  className="flex w-full items-center justify-center gap-3 border border-slate-300 bg-violet-50/60 px-8 py-3.5 font-mono2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-800 transition-colors duration-300 hover:border-[#6D28D9]/50 hover:bg-violet-50"
                >
                  <Phone className="h-4 w-4" /> Continue with mobile number
                </button>
                <div className="flex items-center gap-4 py-1">
                  <span className="h-px flex-1 bg-slate-100" />
                  <span className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-slate-400">or {mode === "register" ? "sign up" : "sign in"} with email</span>
                  <span className="h-px flex-1 bg-slate-100" />
                </div>
              </>
            )}
            {mode === "register" && (
              <>
                <input data-testid="auth-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
                <input data-testid="auth-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Mobile number (+91…)" inputMode="tel" className={inputCls} />
              </>
            )}
            <input data-testid="auth-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" className={inputCls} />
            {mode !== "forgot" && (
            <input
              data-testid="auth-password-input"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
              className={inputCls}
            />
            )}
            {mode === "login" && (
              <div className="flex justify-end">
                <button data-testid="auth-forgot-link" type="button" onClick={() => switchMode("forgot")} className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-slate-500 transition-colors hover:text-[#6D28D9]">Forgot password?</button>
              </div>
            )}
            {error && <p data-testid="auth-error" className="text-xs text-rose-600">{error}</p>}
            <button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#6D28D9] px-8 py-4 font-mono2 text-xs font-semibold uppercase tracking-[0.22em] text-[#FFFFFF] transition-colors duration-300 hover:bg-[#5B21B6] disabled:opacity-50"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send reset link"}
            </button>
            <button
              data-testid="auth-toggle-mode"
              type="button"
              onClick={() => switchMode(mode === "login" ? "register" : "login")}
              className="w-full pt-2 text-center font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-[#6D28D9]"
            >
              {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
            </button>
          </form>
          )}

          <Link to="/" className="mt-6 flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#6D28D9]">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to site
          </Link>
        </motion.div>
      </main>
    </div>
  );
}
